/**
 * Terrain Type Section Component
 * Displays terrain types in a two-column text layout
 * Matches Figma design: no images, just text in two columns
 * Includes Navigation Difficulty subsection with yellow flag icons
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  brandColors,
} from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { ShowMoreTextButton } from '@/components/ui/show-more-text-button';
import { useTextOverflow } from '@/hooks/useTextOverflow';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface TerrainType {
  id: string;
  title: string;
  description: string;
  image: string | null;
}

export interface NavigationDifficultyItem {
  id: string;
  title: string;
  description: string;
}

export interface TerrainTypeSectionProps {
  terrainTypes: TerrainType[];
  navigationDifficulty?: NavigationDifficultyItem[];
  className?: string;
}

/**
 * Yellow Flag Icon Component
 * Matches Figma design for navigation difficulty indicators
 */
const YellowFlagIcon: React.FC = () => {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={{ flexShrink: 0 }}
    >
      <path
        d='M3.33334 3.33333V18.3333'
        stroke={brandColors.primary}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M3.33334 3.33333L8.33334 5.83333L13.3333 3.33333V10.8333L8.33334 13.3333L3.33334 10.8333V3.33333Z'
        fill={brandColors.primary}
        stroke={brandColors.primary}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

/**
 * Terrain Type Card Component
 * Reusable card component for terrain types with Show More functionality
 */
const TerrainTypeCard: React.FC<{
  terrain: TerrainType;
  isMobile: boolean;
}> = ({ terrain, isMobile }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { textRef, exceedsLines } = useTextOverflow<HTMLParagraphElement>(
    3,
    terrain.description
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: spacing[6], // 24px gap
        width: isMobile ? '100%' : '244px',
        padding: spacing[4], // 16px padding
        borderRadius: '23.026px',
        border: '1px solid #E5E7EB',
        backgroundColor: '#FFFFFF',
        flexShrink: 0, // Prevent cards from shrinking in horizontal scroll
      }}
    >
      <h4
        style={{
          fontFamily: fontPrimary,
          fontSize: typography.size.base,
          fontWeight: typography.weight.semibold,
          color: typographyColors.primary,
          margin: 0,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {terrain.title}
      </h4>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[1],
          width: '100%',
          flexGrow: 1,
        }}
      >
        <motion.p
          ref={textRef}
          animate={{
            WebkitLineClamp: isExpanded ? 'none' : 3,
          }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
          }}
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.regular,
            color: typographyColors.text,
            lineHeight: typography.lineHeight.relaxed,
            margin: 0,
            width: '100%',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'none' : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {terrain.description}
        </motion.p>
        {exceedsLines && (
          <ShowMoreTextButton
            isExpanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
            style={isExpanded ? { marginTop: 'auto' } : {}}
          />
        )}
      </div>
    </div>
  );
};

export const TerrainTypeSection: React.FC<TerrainTypeSectionProps> = ({
  terrainTypes,
  navigationDifficulty,
  className,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (!terrainTypes || terrainTypes.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
      className={className}
    >
      <SectionHeader title='Terrain Type' />

      {/* Card Layout - Two columns if 2 or fewer, horizontal scroll if more */}
      <div
        style={{
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
        }}
      >
        {terrainTypes.length <= 2 ? (
          // Grid layout for 2 or fewer items - single column on mobile
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: spacing[4],
              width: '100%',
            }}
          >
            {terrainTypes.map(terrain => (
              <TerrainTypeCard
                key={terrain.id}
                terrain={terrain}
                isMobile={isMobile}
              />
            ))}
          </div>
        ) : (
          // Horizontal scrollable layout for more than 2 items
          <div
            style={{
              display: 'flex',
              gap: spacing[4],
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: `${backgroundColors.shade} transparent`,
              WebkitOverflowScrolling: 'touch',
              paddingBottom: spacing[2], // Space for scrollbar
            }}
          >
            {terrainTypes.map(terrain => (
              <TerrainTypeCard
                key={terrain.id}
                terrain={terrain}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}
      </div>

      {/* Navigation Difficulty Subsection */}
      {navigationDifficulty && navigationDifficulty.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[3],
            padding: `0 ${spacing[3]}px ${spacing[3]}px`,
            marginTop: spacing[2],
          }}
        >
          <h4
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              fontWeight: typography.weight.semibold,
              color: typographyColors.primary,
              margin: 0,
              marginBottom: spacing[2],
            }}
          >
            Navigation Difficulty
          </h4>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[3],
            }}
          >
            {navigationDifficulty.map(item => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: spacing[3],
                  width: '100%',
                }}
              >
                {/* Title on the left */}
                <h5
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.sm,
                    fontWeight: typography.weight.semibold,
                    color: typographyColors.primary,
                    margin: 0,
                    minWidth: '120px',
                    flexShrink: 0,
                  }}
                >
                  {item.title}
                </h5>

                {/* Text field (description) in the middle */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: `${spacing[2]}px ${spacing[3]}px`,
                    backgroundColor: backgroundColors.primary,
                    borderColor: backgroundColors.shade,
                    borderWidth: borders.width.thin,
                    borderStyle: 'solid',
                    borderRadius: borders.radius.md,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <p
                    style={{
                      fontFamily: fontPrimary,
                      fontSize: typography.size.sm,
                      fontWeight: typography.weight.regular,
                      color: typographyColors.text,
                      lineHeight: typography.lineHeight.relaxed,
                      margin: 0,
                      width: '100%',
                    }}
                  >
                    {item.description}
                  </p>
                </div>

                {/* Yellow flag icon on the right */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <YellowFlagIcon />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
